import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatArea, UNIT_TYPE_LABELS } from '@blimo/shared';
import { DataTable, ErrorState, PageHeader, SearchInput } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../components/filter-bar.jsx';
import { PropertyLink, UnitLabel } from '../../components/data.jsx';
import { useListState } from '../../lib/list-search.js';

const TYPE_OPTIONS = Object.entries(UNIT_TYPE_LABELS).map(([value, label]) => ({ value, label }));

export function UnitsListPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = { ...list.apiParams, filter: { q: search.q, type: search.type, property_id: search.property } };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.units(api, params));
  const columns = useMemo(
    () => [
      { accessorKey: 'number', header: 'Nr.', cell: ({ row }) => <UnitLabel unit={{ id: row.original.id, number: row.original.number }} /> },
      { accessorKey: 'designation', header: 'Bezeichnung', cell: ({ row }) => row.original.designation ?? '–' },
      { id: 'property', header: 'Liegenschaft', enableSorting: false, cell: ({ row }) => <PropertyLink property={row.original.property} /> },
      { id: 'type', header: 'Typ', enableSorting: false, cell: ({ row }) => row.original.type?.label ?? row.original.source_type_label ?? '–' },
      { accessorKey: 'living_space', header: 'Wohnfläche', meta: { numeric: true }, cell: ({ row }) => formatArea(row.original.living_space) },
    ],
    [],
  );

  return (
    <>
      <PageHeader title="Einheiten" description="Wohnungen, Gewerbe, Stellplätze und weitere Einheiten aller Liegenschaften." />
      <FilterBar>
        <SearchInput
          label="Einheiten durchsuchen"
          placeholder="Nummer oder Bezeichnung …"
          value={search.q ?? ''}
          onValueChange={(v) => list.setFilter('q', v)}
        />
        <FilterSelect label="Typ" value={search.type} onChange={(v) => list.setFilter('type', v)} options={TYPE_OPTIONS} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Einheiten"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          sorting={list.sorting}
          onSortingChange={list.onSortingChange}
          emptyTitle="Keine Einheiten gefunden"
        />
      )}
    </>
  );
}
